import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { TrackingEvent } from '@marketplace-web/observability/event-tracker-data'

export type RegistrationBehaviourAction = 'start' | 'click' | 'submit' | 'end'

export type RegistrationBehaviourClickActionDetails =
  | 'privacy-policy-link'
  | 'terms-and-conditions-link'
  | 'registration-faq-link'

export type RegistrationBehaviourEndActionDetails = 'modal-closed' | 'page-closed'

type RegistrationBehaviourSubmitActionDetails = 'successful' | 'unsuccessful'

export type RegistrationBehaviourActionDetails =
  | RegistrationBehaviourClickActionDetails
  | RegistrationBehaviourSubmitActionDetails
  | RegistrationBehaviourEndActionDetails

export type RegistrationType = 'email' | 'apple' | 'google' | 'facebook'

type AuthType = 'internal' | 'facebook' | 'google' | 'apple'

type UserRegisterSuccessEventArgs = {
  type: AuthType
  userId?: number
}

type UserRegisterSuccessEventExtra = {
  type: AuthType
}

export const userRegisterSuccessEvent = (args: UserRegisterSuccessEventArgs): TrackingEvent => {
  const { type, userId } = args

  const extra: UserRegisterSuccessEventExtra = { type }

  return { event: 'user.register_success', extra, userId }
}

type UserRegisterFailEventExtra = {
  type: AuthType
  reason: string
  details?: string
}

type UserRegisterFailEventArgs = {
  type: AuthType
  errors?: Array<ErrorItem>
}

export const userRegisterFailEvent = (args: UserRegisterFailEventArgs): TrackingEvent => {
  const { type, errors } = args

  const extra: UserRegisterFailEventExtra = {
    type,
    reason: 'validation_error',
    details: errors?.map(error => error.value).join(', '),
  }

  return { event: 'user.register_fail', extra }
}

type AuthenticateSuccessEventArgs = {
  type: AuthType
  userId?: number
}

type AuthenticateSuccessEventExtra = {
  type: AuthType
}

export const authenticateSuccessEvent = (args: AuthenticateSuccessEventArgs): TrackingEvent => {
  const { type, userId } = args

  const extra: AuthenticateSuccessEventExtra = { type }

  return { event: 'user.authenticate_success', extra, userId }
}

type AuthenticateFailEventExtra = {
  type: AuthType
  reason: string
  details?: string
}

type AuthenticateFailEventArgs = {
  type: AuthType
  errors?: Array<ErrorItem>
  error?: string
}

export const authenticateFailEvent = (args: AuthenticateFailEventArgs): TrackingEvent => {
  const { type, errors, error } = args

  const extra: AuthenticateFailEventExtra = {
    type,
    reason: 'validation_error',
    details: errors?.map(({ value }) => value).join(', ') || error,
  }

  return { event: 'user.authenticate_fail', extra }
}

type RegistrationFormBehaviourEventArgs = {
  taskId: string
  fingerprint: string
  countryCode: string
  screen: string
  authenticationType: RegistrationType
  action: RegistrationBehaviourAction
  actionDetails?: RegistrationBehaviourActionDetails
  inApp: boolean
  userId?: number
}

export const registrationFormBehaviourEvent = ({
  taskId,
  fingerprint,
  countryCode,
  screen,
  authenticationType,
  action,
  actionDetails,
  inApp,
  userId,
}: RegistrationFormBehaviourEventArgs): TrackingEvent => {
  return {
    event: 'registration.form_behaviour',
    userId,
    extra: {
      task_id: taskId,
      screen,
      authentication_type: authenticationType,
      action,
      in_app: inApp,
      country_code: countryCode,
      fingerprint,
      action_details: actionDetails,
    },
  }
}
